import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { articles, hotComments } from '@/mocks/articles';
import { categories, hotTags } from '@/mocks/categories';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

function ArticleCard({ article, index }: { article: typeof articles[0]; index: number }) {
  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300/60 transition-all duration-300 hover:-translate-y-1 animate-fade-in-up"
      style={{ animationDelay: `${index * 0.1}s`, opacity: 0 }}
    >
      <div className="relative h-48 sm:h-52 overflow-hidden">
        <img
          src={article.image}
          alt={article.title}
          loading="lazy"
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute top-3 left-3 flex items-center gap-2">
          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary-500 text-background-50">
            {article.categoryName}
          </span>
          {article.isExclusive && (
            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-accent-500 text-background-50">
              独家
            </span>
          )}
        </div>
      </div>
      <div className="p-4 md:p-5">
        <h3 className="font-heading font-bold text-base md:text-lg text-foreground-950 leading-snug mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 leading-relaxed mb-3 line-clamp-2">
          {article.summary}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line" />
              {article.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {(article.views / 10000).toFixed(1)}万
            </span>
          </div>
          <span className="flex items-center gap-1">
            <i className="ri-time-line" />
            {article.readTime}
          </span>
        </div>
      </div>
    </Link>
  );
}

function ExclusiveCard({ article, index }: { article: typeof articles[0]; index: number }) {
  return (
    <Link
      to={`/article/${article.slug}`}
      className="group relative block rounded-xl overflow-hidden h-80 md:h-96 animate-fade-in-up"
      style={{ animationDelay: `${index * 0.15}s`, opacity: 0 }}
    >
      <img
        src={article.image}
        alt={article.title}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/90 via-foreground-950/40 to-transparent" />
      <div className="absolute top-3 left-3">
        <span className="px-3 py-1.5 rounded-full text-xs font-semibold bg-accent-500 text-background-50">
          独家猛料
        </span>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
        <h3 className="font-heading font-bold text-lg md:text-xl text-background-50 leading-snug mb-2 group-hover:text-accent-300 transition-colors">
          {article.title}
        </h3>
        <p className="text-sm text-background-200/80 leading-relaxed line-clamp-2 mb-3">
          {article.summary}
        </p>
        <div className="flex items-center gap-3 text-xs text-background-300">
          <span className="flex items-center gap-1">
            <i className="ri-fire-line" />
            {(article.views / 10000).toFixed(1)}万阅读
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-heart-line" />
            {article.likes.toLocaleString()}点赞
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function Home() {
  const hotArticles = articles.filter((a) => a.isHot).slice(0, 4);
  const exclusiveArticles = articles.filter((a) => a.isExclusive).slice(0, 3);

  useEffect(() => {
    document.title = '51扒瓜网 - 全天候追踪最新娱乐八卦与网络热点大瓜独家猛料';
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[520px] md:min-h-[600px] flex flex-col justify-end overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Abstract%20dramatic%20theater%20spotlight%20stage%20with%20red%20and%20gold%20light%20beams%2C%20smoke%20effects%2C%20entertainment%20show%20atmosphere%2C%20cinematic%20wide%20shot%2C%20dark%20moody%20background%20with%20warm%20amber%20accents%2C%20high%20contrast&width=1600&height=900&seq=hero-bg&orientation=landscape"
          alt="51扒瓜网"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/50 via-foreground-950/60 to-foreground-950/80" />

        <div className="relative z-10 px-4 md:px-8 lg:px-12 pb-8 md:pb-12 pt-32">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 mb-6 animate-fade-in-up">
              <span className="w-2 h-2 rounded-full bg-accent-400 animate-pulse" />
              <span className="text-sm text-background-100 font-medium">
                今日已更新 15 条独家爆料
              </span>
            </div>

            <h1 className="font-heading font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-background-50 leading-tight mb-5 animate-fade-in-up stagger-1">
              全天候追踪
              <br />
              最新娱乐八卦
              <br />
              <span className="text-accent-400">与网络热点大瓜</span>
            </h1>

            <p className="text-base md:text-lg text-background-200 max-w-xl leading-relaxed mb-8 animate-fade-in-up stagger-2">
              独家挖掘大瓜猛料，吃瓜内容每日不断更新。娱乐八卦爱好者的集中地，第一手资讯，最快爆料速度。
            </p>

            <div className="flex flex-wrap items-center gap-4 animate-fade-in-up stagger-3">
              <Link
                to="/category/yule-bagua"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-fire-line" />
                热门八卦
              </Link>
              <Link
                to="/category/dujia-mengliao"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white/10 backdrop-blur-sm text-background-50 font-semibold text-sm border border-white/20 hover:bg-white/20 transition-colors whitespace-nowrap"
              >
                <i className="ri-vip-crown-line" />
                独家猛料
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Hot Articles Grid */}
      <section className="px-4 md:px-8 lg:px-12 py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-10">
            <div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-2">
                热门吃瓜文章
              </h2>
              <p className="text-sm md:text-base text-foreground-600">
                全网最热话题，第一手爆料，让瓜友们永远走在吃瓜最前线
              </p>
            </div>
            <Link
              to="/category/yule-bagua"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors shrink-0"
            >
              查看更多
              <i className="ri-arrow-right-line" />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {hotArticles.map((article, i) => (
              <ArticleCard key={article.id} article={article} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Exclusive Section */}
      <section className="px-4 md:px-8 lg:px-12 py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-2">
              独家重磅大瓜 <span className="text-accent-500">独家挖掘</span> 第一手猛料
            </h2>
            <p className="text-sm md:text-base text-foreground-600">
              51扒瓜网独家爆料，让瓜友们永远走在吃瓜最前线
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            {exclusiveArticles.map((article, i) => (
              <ExclusiveCard key={article.id} article={article} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="px-4 md:px-8 lg:px-12 py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-10">
            <div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-2">
                热门分类频道
              </h2>
              <p className="text-sm md:text-base text-foreground-600">
                四大频道覆盖娱乐八卦全领域，精准定位你最关心的吃瓜内容
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {categories.map((cat, i) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="group block p-6 rounded-xl border border-background-200/70 bg-background-50 hover:border-primary-300/60 hover:shadow-sm transition-all duration-300 animate-fade-in-up"
                style={{ animationDelay: `${i * 0.1}s`, opacity: 0 }}
              >
                <div className={`w-12 h-12 rounded-xl ${cat.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <i className="ri-folder-open-line text-background-50 text-xl" />
                </div>
                <h3 className="font-heading font-bold text-lg text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors">
                  {cat.name}
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  {cat.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-foreground-500">
                    {cat.articleCount} 篇文章
                  </span>
                  <span className="w-8 h-8 rounded-full bg-background-100 flex items-center justify-center text-foreground-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                    <i className="ri-arrow-right-line text-sm" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Hot Tags */}
      <section className="px-4 md:px-8 lg:px-12 py-10 md:py-14 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950 mb-6">
            热门吃瓜标签
          </h2>
          <div className="flex flex-wrap gap-3">
            {hotTags.map((tag) => (
              <span
                key={tag}
                className="px-4 py-2 rounded-full text-sm font-medium bg-secondary-100 text-secondary-900 border border-secondary-200 hover:bg-secondary-200 transition-colors cursor-pointer"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Comments Section */}
      <section className="px-4 md:px-8 lg:px-12 py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="w-2 h-2 rounded-full bg-accent-500" />
              <span className="text-sm font-medium text-accent-600">网友真实评价</span>
            </div>
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-3">
              瓜友们都在说
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto">
              来自全国各地娱乐八卦爱好者的真实反馈
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {hotComments.map((comment, i) => (
              <div
                key={comment.id}
                className="bg-background-50 rounded-xl p-5 md:p-6 border border-background-200/70 animate-fade-in-up"
                style={{ animationDelay: `${i * 0.1}s`, opacity: 0 }}
              >
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: comment.rating }).map((_, j) => (
                    <i key={j} className="ri-star-fill text-accent-400 text-sm" />
                  ))}
                  {Array.from({ length: 5 - comment.rating }).map((_, j) => (
                    <i key={j} className="ri-star-line text-accent-400/40 text-sm" />
                  ))}
                </div>
                <h4 className="font-heading font-semibold text-base text-foreground-950 mb-2">
                  {comment.content.slice(0, 20)}...
                </h4>
                <p className="text-sm text-foreground-600 leading-relaxed mb-5 line-clamp-4">
                  {comment.content}
                </p>
                <div className="flex items-center gap-3">
                  <img
                    src={comment.avatar}
                    alt={comment.username}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <p className="text-sm font-semibold text-foreground-900">{comment.username}</p>
                    <p className="text-xs text-foreground-500">{comment.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 md:px-8 lg:px-12 py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="bg-primary-500 rounded-2xl p-8 md:p-12 lg:p-16 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-accent-500/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-secondary-500/20 rounded-full blur-3xl" />
            <div className="relative z-10">
              <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-background-50 mb-4">
                不想错过任何大瓜？
              </h2>
              <p className="text-base md:text-lg text-background-200 max-w-xl mx-auto mb-8">
                关注51扒瓜网，每日获取最新娱乐八卦与网络热点，让吃瓜成为一种生活方式。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/category/yule-bagua"
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-lg bg-background-50 text-primary-600 font-semibold text-sm hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  <i className="ri-article-line" />
                  浏览全部文章
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 px-8 py-3.5 rounded-lg bg-white/10 text-background-50 font-semibold text-sm border border-white/20 hover:bg-white/20 transition-colors whitespace-nowrap"
                >
                  <i className="ri-information-line" />
                  了解更多
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: '51扒瓜网首页',
          description: '全天候追踪娱乐圈最新八卦和网络热点，独家挖掘大瓜猛料',
          url: 'http://rkldzswyxgs.com/',
          isPartOf: {
            '@type': 'WebSite',
            name: '51扒瓜网',
            url: 'http://rkldzswyxgs.com/',
          },
          mainEntity: {
            '@type': 'ItemList',
            itemListElement: articles.slice(0, 4).map((a, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              url: `http://rkldzswyxgs.com/article/${a.slug}`,
              name: a.title,
            })),
          },
        })}
      </script>

      <Footer />
    </div>
  );
}